import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

// Remote url fetch to bypass canvas CORS error (not working)
// more info at:
// https://developer.mozilla.org/en-US/docs/Web/HTML/How_to/CORS_enabled_image
const fetchImage = async (url) => {
  try {
    const response = await fetch(url, { mode: "cors" });
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.log("CORS error:", error);
  }
};

const createImage = async (url) => {
  const image = new Image();

  let imageUrl = url;

  // if image is from external source, fetch it
  if (!url.startsWith("data:") && !url.startsWith("blob:")) {
    imageUrl = await fetchImage(url);
  }

  return new Promise((resolve, reject) => {
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.src = imageUrl;
  });
};

/*const createImage = async (url) => 
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues
    image.src = url;
  });*/

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

// Returns the new bounding area of a rotated rectangle.
function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

// This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
  cropShape = "rect"
) {
  const image = await createImage(imageSrc);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");

  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Rounded crop
  if (cropShape === "round") {
    const centerX = pixelCrop.width / 2;
    const centerY = pixelCrop.height / 2;
    const radius = Math.min(pixelCrop.width, pixelCrop.height) / 2;

    croppedCtx.beginPath();
    croppedCtx.arc(centerX, centerY, radius, 0, 2 * Math.PI, false);
    croppedCtx.clip();
  }

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // As Base64 string (DataURL)
  return croppedCanvas.toDataURL("image/png");

  // As a blob
  /*return new Promise((resolve, reject) => {
    croppedCanvas.toBlob((file) => {
      resolve(URL.createObjectURL(file));
    }, "image/jpeg");
  });*/
}

const ImageEditor = ({
  cropShape = "rect",
  image,
  originalImage,
  onClose,
  onCropSave,
  open,
  initialCropWidth,
  initialCropHeight,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [localCropShape, setLocalCropShape] = useState(cropShape);
  //const [localAspectRatio, setLocalAspectRatio] = useState(1);
  const [cropSize, setCropSize] = useState();

  useEffect(() => {
    if (!image || !open) {
      setCropSize(null);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const imageWidth = img.naturalWidth;
      const imageHeight = img.naturalHeight;

      let cropWidth = 0;
      let cropHeight = 0;
      let hasInitialCrop = false;

      if (
        initialCropWidth &&
        initialCropHeight &&
        typeof initialCropWidth === "string" &&
        typeof initialCropHeight === "string"
      ) {
        cropWidth = parseInt(initialCropWidth.replace("px", ""));
        cropHeight = parseInt(initialCropHeight.replace("px", ""));
        hasInitialCrop = true;
      } else {
        cropWidth = imageWidth;
        cropHeight = imageHeight;
      }

      let newCropWidth = cropWidth;
      let newCropHeight = cropHeight;
      let calculatedZoom = 1;

      if (cropWidth > 500 || cropHeight > 200) {
        const widthScale = cropWidth > 500 ? 500 / cropWidth : 1;
        const heightScale = cropHeight > 200 ? 200 / cropHeight : 1;

        const scale = Math.min(widthScale, heightScale);

        newCropWidth = Math.min(cropWidth * scale, 500);
        newCropHeight = Math.min(cropHeight * scale, 200);
      }

      // Calculate zoom to ensure image fits within crop area
      if (hasInitialCrop) {
        const widthRatio = cropWidth / imageWidth;
        const heightRatio = cropHeight / imageHeight;

        // Use Math.min to fit image within crop area (less zoom)
        calculatedZoom = Math.min(widthRatio, heightRatio);

        calculatedZoom = Math.max(calculatedZoom, 0.1);
      } else {
        if (cropWidth > 500 || cropHeight > 200) {
          const zoomWidth = newCropWidth / cropWidth;
          const zoomHeight = newCropHeight / cropHeight;
          calculatedZoom = Math.min(zoomWidth, zoomHeight);
          calculatedZoom = Math.max(calculatedZoom, 0.1);
        } else {
          calculatedZoom = 1;
        }
      }

      setCropSize({ height: newCropHeight, width: newCropWidth });
      // Slightly reduce zoom for better alignment
      setZoom(calculatedZoom * 0.98);
      // Reset crop position to center
      setCrop({ x: 0, y: 0 });
    };
    img.onerror = () => {
      setCropSize(null);
      console.log("Error extracting image dimensions");
    };
    img.src = image;
  }, [image, open, initialCropWidth, initialCropHeight]);

  useEffect(() => {
    if (!open) {
      setCropSize(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setRotation(0);
      setCroppedAreaPixels(null);
      setLocalCropShape(cropShape);
    }
  }, [open]);

  const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (!image || !croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(
        image,
        croppedAreaPixels,
        rotation,
        { horizontal: false, vertical: false },
        localCropShape
      );

      if (onCropSave) {
        onCropSave({ url: croppedImage });
      }
    } catch (e) {
      console.error(e);
    }
  }, [image, croppedAreaPixels, rotation, localCropShape, onCropSave]);

  const handleCropShapeChange = (event) => {
    setLocalCropShape(event.target.value);
    console.log("localCropShape", event.target.value);
  };

  const onCropSizeChange = (e) => {
    if (e && typeof e.width === "number" && typeof e.height === "number") {
      setCropSize(e);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>Crop image</DialogTitle>
      <DialogContent style={{ minHeight: 500 }}>
        <div style={{ position: "relative", width: "100%", minHeight: 340 }}>
          <Cropper
            //aspect={localAspectRatio}
            crop={crop}
            cropShape={localCropShape}
            cropSize={cropSize}
            image={image}
            minZoom={0.1}
            restrictPosition={false}
            rotation={rotation}
            showGrid={true}
            zoom={zoom}
            zoomSpeed={0.05}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onCropSizeChange={onCropSizeChange}
            onRotationChange={setRotation}
            onZoomChange={setZoom}
          />
        </div>
        <Grid
          container
          spacing={4}
          style={{
            alignItems: "flex-start",
            display: "flex",
            flexDirection: "row",
            marginTop: 10,
          }}
        >
          <Grid item size={3}>
            <Typography gutterBottom variant="body2">
              Restore image
            </Typography>
            <Button
              color="primary"
              disabled={!originalImage}
              variant="contained"
              onClick={() => {
                if (originalImage && onCropSave) {
                  onCropSave({ url: originalImage, source: "restore" });
                }
              }}
            >
              Restore
            </Button>
          </Grid>
          <Grid item size={3}>
            <Typography gutterBottom variant="body2">
              Crop Shape
            </Typography>
            <FormControl fullWidth>
              <Select
                fullWidth
                id="shape-select"
                style={{ width: "100%" }}
                value={localCropShape}
                onChange={handleCropShapeChange}
              >
                <MenuItem value={"rect"}>Rectangle</MenuItem>
                <MenuItem value={"round"}>Rounded</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/*<Grid item size={3}>
            <Typography gutterBottom variant="body2">
              Aspect Ratio
            </Typography>
            <FormControl fullWidth>
              <Select
                fullWidth
                id="aspect-ratio-select"
                style={{ width: "100%" }}
                value={localAspectRatio}
                onChange={handleAspectRatioChange}
              >
                <MenuItem value={1}>1:1 (Square)</MenuItem>
                <MenuItem value={4 / 3}>4:3 (Rectangle)</MenuItem>
                <MenuItem value={3 / 4}>3:4 (Vertical)</MenuItem>
              </Select>
            </FormControl>
          </Grid>*/}

          <Grid item size={3}>
            <Typography gutterBottom variant="body2">
              Rotation
            </Typography>
            <FormControl fullWidth style={{ width: "100%" }}>
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  width: "100%",
                }}
              >
                <TextField
                  inputProps={{
                    max: 360,
                    min: 0,
                    step: 1,
                  }}
                  size="small"
                  style={{ flexShrink: 0, width: "100px" }}
                  type="number"
                  value={rotation}
                  variant="outlined"
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setRotation(Math.max(0, Math.min(360, value)));
                  }}
                />
                {/*<Slider
                  max={360}
                  min={0}
                  step={1}
                  style={{ flex: 1, width: "100%" }}
                  value={rotation}
                  onChange={(e, rotation) => setRotation(rotation)}
                />*/}
              </div>
            </FormControl>
          </Grid>

          <Grid item size={3}>
            <Typography gutterBottom variant="body2">
              Crop Size
            </Typography>
            <FormControl fullWidth style={{ width: "100%" }}>
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  width: "100%",
                }}
              >
                <TextField
                  inputProps={{ min: 1 }}
                  label="Width"
                  size="small"
                  style={{ flexShrink: 0, width: "100px" }}
                  type="number"
                  value={cropSize?.width ? cropSize.width : 0}
                  variant="outlined"
                  onChange={(e) => {
                    setCropSize((cropSize) => ({
                      ...cropSize,
                      width: parseInt(e.target.value) || 0,
                    }));
                  }}
                />
                <TextField
                  inputProps={{ min: 1 }}
                  label="Height"
                  size="small"
                  style={{ flexShrink: 0, width: "100px" }}
                  type="number"
                  value={cropSize?.height ? cropSize.height : 0}
                  variant="outlined"
                  onChange={(e) => {
                    setCropSize((cropSize) => ({
                      ...cropSize,
                      height: parseInt(e.target.value) || 0,
                    }));
                  }}
                />
              </div>
            </FormControl>
          </Grid>

          <Grid item size={3}>
            <Typography gutterBottom variant="body2">
              Zoom
            </Typography>
            <FormControl fullWidth style={{ width: "100%" }}>
              <div
                style={{
                  alignItems: "flex-start",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  width: "100%",
                }}
              >
                <TextField
                  inputProps={{
                    max: 3,
                    min: 0.1,
                    step: 0.05,
                  }}
                  size="small"
                  style={{ flexShrink: 0, width: "100px" }}
                  type="number"
                  value={zoom.toFixed(2)}
                  variant="outlined"
                  onChange={(e) => {
                    const value = parseFloat(
                      parseFloat(e.target.value).toFixed(2) || 0
                    );
                    setZoom(value);
                  }}
                />
                <Typography color="textSecondary" variant="caption">
                  Use the mouse wheel to zoom
                </Typography>
              </div>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose}>Close</Button>
        <Button color="primary" variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageEditor;
